import { forwardRef, type ButtonHTMLAttributes } from 'react';
import type { IconName } from '../engine/types';
import { cx } from '../utils/classnames';
import { Icon } from './Icon';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  block?: boolean;
  loading?: boolean;
  iconLeft?: IconName;
  iconRight?: IconName;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', block, loading, iconLeft, iconRight, className, children, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      data-loading={loading || undefined}
      className={cx(
        'cv-btn',
        variant !== 'secondary' && `cv-btn--${variant}`,
        size !== 'md' && `cv-btn--${size}`,
        block && 'cv-btn--block',
        className,
      )}
      {...rest}
    >
      {iconLeft ? <Icon name={iconLeft} size={size === 'lg' ? 20 : 18} /> : null}
      {children}
      {iconRight ? <Icon name={iconRight} size={size === 'lg' ? 20 : 18} /> : null}
    </button>
  );
});

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: IconName;
  label: string;
  size?: number;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon, label, size = 22, className, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cx('cv-icon-btn', className)}
      aria-label={label}
      title={label}
      {...rest}
    >
      <Icon name={icon} size={size} />
    </button>
  );
});
